/**
 * Turning an RFC 9457 problem+json body into a sentence the user can act on.
 *
 * CLAUDE.md §7 gives every failure a stable machine `code` — `ERR_PERIOD_CLOSED`,
 * `ERR_SOD_VIOLATION`, `ERR_ROW_VERSION`. The code is the contract; the prose is
 * not. So the client keys its own sentence off the code and only falls back to
 * the server's `detail` when the code is one it has no wording for. That is what
 * makes an error message translatable at all: `detail` arrives in whatever
 * language the server rendered, and a Dhivehi user would otherwise get an
 * English sentence in the middle of a Dhivehi screen.
 *
 * Field-level errors (`errors[]`) stay as the server wrote them and are mapped
 * onto the offending control by `EField` — those are per-field validation
 * messages the server localises from the same catalogue.
 */

import { toProblemDetails } from '@erpx/ui'

import { i18n } from '@/i18n'

/** `errors.codes.<CODE>` for a code the catalogue has wording for. */
function keyForCode(code: string | undefined): string | null {
  if (code === undefined || code === '') return null
  const key = `errors.codes.${code}`
  return i18n.global.te(key) ? key : null
}

/**
 * A human sentence for a thrown API error.
 *
 * The precedence deserves an explanation, because it is not obvious.
 *
 * The server's `detail` is prose, and prose has a language. On an English
 * screen it is in the right language *and* it is usually more specific than
 * anything keyed off a code could be — `ERR_UNAUTHENTICATED` covers both a
 * mistyped password and an expired session, and the server knows which one
 * happened. So in `en`, `detail` wins.
 *
 * On a Dhivehi screen that same sentence is an English paragraph in the middle
 * of translated chrome, which is precisely the failure §6 exists to prevent. So
 * outside `en` the catalogue's sentence for the code wins and the detail is
 * dropped. The code is the stable contract; the prose is not.
 *
 * Either way: never raw JSON, never a bare status, never empty.
 */
export function problemText(error: unknown, fallbackKey = 'errors.generic'): string {
  const problem = toProblemDetails(error)
  const key = keyForCode(problem?.code)
  const readsEnglish = i18n.global.locale.value === 'en'

  if (key !== null && !readsEnglish) return i18n.global.t(key)

  const detail = problem?.detail?.trim()
  if (detail !== undefined && detail !== '') return detail

  if (key !== null) return i18n.global.t(key)

  const title = problem?.title?.trim()
  if (title !== undefined && title !== '') return title

  if (error instanceof Error && error.message.trim() !== '') return error.message

  return i18n.global.t(fallbackKey)
}

/** Whether the catalogue has dedicated wording for this problem's code. */
export function hasLocalisedCode(error: unknown): boolean {
  return keyForCode(toProblemDetails(error)?.code) !== null
}
